/**
 * Parsing and answer-shaping for the coding agent's `ask_user` tool.
 *
 * A pending question is not live state: the tool *ends* the agent's turn rather than blocking it,
 * so the question outlives the socket, the loop and the sandbox. What remains is one tool call
 * whose `input` holds the questions and whose `output` is still null. Both the live card and the
 * transcript record are read out of that single pair, which is why everything here works off tool
 * call payloads and never off an event.
 *
 * Parsing is deliberately forgiving and returns `null` rather than throwing: a payload we cannot
 * read should fall back to the generic tool row, never break the thread.
 */

/** Past this the pills stop being scannable and the answer is really free text. */
const MAX_OPTIONS = 5;
export interface AskQuestion {
  /** 1-3 words naming the decision. Used as the eyebrow when a card carries several questions. */
  header: string;
  question: string;
  options: string[];
  /** Whether several options may be picked at once. The agent chooses this per question. */
  multiSelect: boolean;
}

/** One answer, positional against the questions. Empty on both counts means "you decide". */
export interface AskAnswer {
  selected: string[];
  text: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  // Tool payloads reach us as parsed JSON, but `output` is a string the agent wrote.
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return null;
    }
  }
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** The questions out of an `ask_user` tool call's `input`. */
export function parseAskQuestions(input: unknown): AskQuestion[] | null {
  const raw = asRecord(input);
  if (!raw || !Array.isArray(raw.questions)) return null;

  const questions: AskQuestion[] = [];
  for (const entry of raw.questions) {
    const record = asRecord(entry);
    if (!record) continue;
    const question = cleanString(record.question);
    if (!question) continue;
    questions.push({
      header: cleanString(record.header) || 'Question',
      question,
      options: Array.isArray(record.options)
        ? record.options.map(cleanString).filter(Boolean).slice(0, MAX_OPTIONS)
        : [],
      multiSelect: record.multiSelect === true,
    });
  }
  return questions.length > 0 ? questions : null;
}

/** The answers out of the call's `output`. `null` while the question is still unanswered. */
export function parseAskAnswers(output: string | null): AskAnswer[] | null {
  if (!output) return null;
  const raw = asRecord(output);
  if (!raw || !Array.isArray(raw.answers)) return null;

  return raw.answers.map((entry) => {
    const record = asRecord(entry);
    return {
      selected: Array.isArray(record?.selected) ? record.selected.map(cleanString).filter(Boolean) : [],
      text: cleanString(record?.text) || null,
    };
  });
}

/**
 * The user's own words, when they answered the whole card in one message instead of question by
 * question. `null` when they answered per question, or not yet.
 */
export function parseAskReply(output: string | null): string | null {
  return cleanString(asRecord(output)?.reply) || null;
}

// -- Draft state, owned by the card but shaped here so it can be tested ---------------------

export interface AskDraft {
  selected: string[];
  text: string;
  /** Whether the free-text field is showing. It is the escape hatch, not the default. */
  writing: boolean;
  /** The user explicitly handed this one back to the agent, as opposed to not reaching it yet. */
  deferred: boolean;
}

export const emptyDraft = (): AskDraft => ({ selected: [], text: '', writing: false, deferred: false });

export function initialDrafts(questions: AskQuestion[]): AskDraft[] {
  return questions.map(() => emptyDraft());
}

/**
 * Pick an option. Single-select replaces (and un-picks on a second click); multi-select
 * accumulates.
 *
 * Picking always closes the free-text field and clears what was typed there: writing your own
 * answer and choosing one of the offered ones are alternatives, not things to combine. Keeping
 * hidden text would also silently count as an answer, which is worse than losing it.
 */
export function toggleOption(draft: AskDraft, option: string, multiSelect: boolean): AskDraft {
  const selected = multiSelect
    ? draft.selected.includes(option)
      ? draft.selected.filter((s) => s !== option)
      : [...draft.selected, option]
    : draft.selected[0] === option
      ? []
      : [option];
  return { selected, text: '', writing: false, deferred: false };
}

/** Open (or close) the free-text field. Opening it clears the picks — see {@link toggleOption}. */
export function toggleWriting(draft: AskDraft): AskDraft {
  if (draft.writing) return { ...draft, text: '', writing: false };
  return { selected: [], text: draft.text, writing: true, deferred: false };
}

/** Hand this question back to the agent, discarding whatever was drafted for it. */
export function deferDraft(): AskDraft {
  return { ...emptyDraft(), deferred: true };
}

/** Whether the user actually said something, as opposed to deferring or not reaching it. */
export function isAnswered(draft: AskDraft): boolean {
  return draft.selected.length > 0 || draft.text.trim().length > 0;
}

/** Whether the user has dealt with this question at all, either by answering or by deferring. */
export function isAddressed(draft: AskDraft): boolean {
  return isAnswered(draft) || draft.deferred;
}

/**
 * Build the answers to send. Positional, so an untouched question keeps its slot as an empty
 * answer — the agent reads that as "decide this one yourself" rather than as a missing question.
 */
export function buildAnswers(questions: AskQuestion[], drafts: AskDraft[]): AskAnswer[] {
  return questions.map((question, i) => {
    const draft = drafts[i] ?? emptyDraft();
    return {
      // Ordered as displayed rather than as clicked: the agent should read the answer the way
      // the user saw it.
      selected: question.options.filter((o) => draft.selected.includes(o)),
      text: draft.text.trim() || null,
    };
  });
}

/** One-line rendering of an answer, for the transcript. */
export function summarizeAnswer(answer: AskAnswer | undefined): string {
  if (!answer) return 'The agent decided';
  const picked = answer.selected.join(' · ');
  if (picked && answer.text) return `${picked} — ${answer.text}`;
  return picked || answer.text || 'The agent decided';
}
